/*
  customsirge_motion.js
  Purpose:  The run's single motion runtime — reveal (+ side reveal + stagger),
            marquee, count-up, float and parallax, every behaviour driven purely
            by data-* attributes. Registers ONE Customsirge component ('motion'),
            per docs/CONVENTIONS.md §4 (JS registers, never auto-runs).
  Spec:     decision 0016 (motion spec) + design/ notes; plans/sections.json
            foundation_b.kit_motion ("reveal 0.65s/28px, side reveal, staggered
            grid, marquee, count-up, float; reduced-motion off; Customsirge.register").
            Built while implementing home_hero (plans/sections.json depends_on).
  Data:     none — reads no section/product/page state. Every input arrives as a
            data-* attribute the CONSUMING SECTION writes on its own markup.
  Renders:  nothing on its own. Sets `html.customsirge-motion` once on load (see
            CSS CONTRACT below) and toggles per-element state attributes; the
            CONSUMING SECTION'S OWN stylesheet (assets/customsirge_<section>.css)
            must style them — this file ships no CSS of its own and never will
            (docs/CONVENTIONS.md §2: CSS ships with its section, not a shared
            kit file that every page pays for).
  Owns:     window.Customsirge components['motion']. No CSS namespace.
  Loaded:   Wired into layout/theme.liquid (0038), right after the
            customsirge_kit.js line — every section can rely on it being
            present and no longer needs to load it itself. Any section that
            uses a data-customsirge-{reveal,stagger,marquee,countup,float,
            parallax} attribute just works; customsirge_home_hero.liquid
            (the section that first depended on this file, per
            plans/sections.json foundation_b.kit_motion) no longer carries
            its own <script> tag for it.
  Owner:    builder(home_hero), per plans/sections.json foundation_b.kit_motion

  ─────────────────────────────────────────────────────────────────────────────
  customsirge: DUPLICATE-REGISTRATION NOTICE (read before adding a 7th behaviour)

  customsirge_kit.js (hot — this file's author may not edit it) ALREADY registers
  three components against three of the SAME attribute names this run's brief
  specifies for 'motion': kit's 'reveal' on [data-customsirge-reveal], 'marquee'
  on [data-customsirge-marquee], and 'countup' on [data-customsirge-countup].
  Those are the boilerplate's original, simpler implementations (kit's reveal
  hides content unconditionally via customsirge_kit.css — no progressive-
  enhancement gate — and kit's marquee clones N times instead of once).

  This run's brief calls for a richer, unified contract (translateY(28px)/0.65s,
  left/right variants, delay, a resting state that is VISIBLE without JS, a
  single-duplicate marquee, locale-aware count-up) under ONE registration name,
  'motion', using the SAME attribute names as the brief specifies verbatim. That
  means for any element carrying [data-customsirge-reveal], [data-customsirge-
  marquee] or [data-customsirge-countup], BOTH kit's original component AND this
  one will independently attach (different registration names, so neither one's
  idempotency guard sees the other — Customsirge tracks instances per component
  name per element). In practice this is wasteful (two IntersectionObservers on
  the same element for reveal/countup) but not broken, because:
    - kit.js's countup and this file's countup both read the FINAL value from
      the element's own text/data before animating, so whichever finishes last
      leaves the correct number on screen.
    - kit.js's reveal only ever ADDS the attribute `data-customsirge-revealed`;
      nothing in this file or in a section built against this file's contract
      reads that attribute, so kit's copy is inert noise, not a visible bug.
    - kit.css's unconditional `[data-customsirge-reveal]{opacity:0;transform:
      translateY(16px)}` rule (no progressive-enhancement gate) WOULD hide
      content that never gets JS — a real, pre-existing kit.css defect this
      file cannot fix (hot file). A section using this file's reveal contract
      must write a higher-specificity override
      (`html.customsirge-motion [data-customsirge-reveal]`, see CSS CONTRACT
      below) so kit.css's rule never wins while JS is running; the "JS entirely
      disabled" edge case still inherits kit.css's pre-existing hidden-by-
      default behaviour until kit.css is fixed.

  customsirge: HOT-FILE REQUEST — once every section on this run is migrated to
  customsirge_motion.js, remove kit.js's 'reveal' / 'marquee' / 'countup'
  registrations and kit.css's matching rules (they are fully superseded) so the
  duplicate observers and the unconditional opacity:0 default go away for good.
  Filed instead of silently duplicating logic per BUILD RULES "extend, do not
  duplicate" — this file does not re-implement kit's approach, it replaces it;
  the leftover kit.js/css copies are the thing that needs deleting, not this one.
  ─────────────────────────────────────────────────────────────────────────────

  AUTHOR-FACING API — one data attribute (plus its own config attributes) per
  behaviour. All of it is opt-in markup; nothing here scans the whole page for
  bare classes.

  data-customsirge-reveal            Fade + translateY(28px)→0 over 0.65s
                                      ease-out (decision 0016). Optional value
                                      "left" | "right" for a side reveal —
                                      translateX(±40px)→0 over 0.7s, its own
                                      distance and duration, not the vertical
                                      reveal's 28px/0.65s reused sideways (see
                                      CSS CONTRACT below). Optional
                                      data-delay="180" (ms) on the SAME element.

  data-customsirge-stagger           Container. Every DIRECT CHILD must carry
                                      its own data-customsirge-reveal (with an
                                      optional left/right value) — this
                                      attribute does not add reveal to a child
                                      that doesn't already opt in. The container
                                      computes `data-delay = index * step` for
                                      each child that doesn't already set its
                                      own data-delay, so DOM order is stagger
                                      order. Override the step with
                                      data-step="120" (ms), default 80. A
                                      staggered child's OWN reveal transition
                                      runs 0.55s, not the lone-element 0.65s
                                      (decision 0016; see CSS CONTRACT).

  data-customsirge-marquee           Continuous translateX loop. Needs ONE
                                      child, `[data-customsirge-marquee-track]`,
                                      wrapping the content to repeat — this file
                                      clones that track's current children
                                      ONCE (two copies back to back) and
                                      animates the whole track by exactly minus
                                      one copy's pitch (an item to its own
                                      clone), which is what makes the loop
                                      seamless. One copy only keeps the strip
                                      full while it is at least as wide as the
                                      strip: add data-fill to clone as many
                                      copies as the strip's width needs,
                                      re-counted whenever it resizes.
                                      data-speed="60" sets px/second
                                      (default 60). Pausing on hover is a CSS
                                      concern (see CONTRACT below) — this file
                                      does not touch animation-play-state.

  data-customsirge-countup           Counts 0 → data-to="1240" over
                                      data-duration="1600" (ms, default 1600),
                                      easeOutCubic. data-prefix="$" / data-
                                      suffix="+" wrap the rendered number.
                                      Decimal places are inferred from data-to
                                      (e.g. data-to="4.8" keeps one decimal
                                      throughout the count). Uses
                                      Number.toLocaleString so thousands
                                      grouping matches the visitor's locale
                                      (html lang, else the browser default).

  data-customsirge-float             Gentle continuous ±amplitude Y float,
                                      CSS-@keyframes driven (this file only
                                      supplies the two custom properties the
                                      keyframe reads, so the animation itself
                                      costs nothing on the main thread).
                                      data-amplitude="8" (px, default 8),
                                      data-period="6s" (default "6s" — a bare
                                      number is treated as seconds).

  data-customsirge-parallax="40"     Shifts the element by translate3d(0, ±40px
                                      i.e. the given px value split across the
                                      element's own scroll range, 0,0) as it
                                      crosses the viewport. rAF-throttled,
                                      passive scroll listener, `will-change:
                                      transform` while active. Active ONLY at
                                      ≥990px (--customsirge-bp-lg) — re-checked
                                      on every scroll/resize tick so crossing
                                      the breakpoint live degrades cleanly.

  ALL SIX are no-ops (content simply renders in its authored resting state) when
  `Customsirge.prefersReducedMotion()` is true, and reveal/stagger/countup/
  marquee degrade the same way when IntersectionObserver is unavailable.

  ─────────────────────────────────────────────────────────────────────────────
  CSS CONTRACT — what a CONSUMING SECTION'S stylesheet must write. This file
  ships none of the following; each section that uses an attribute above adds
  the matching rules to its OWN assets/customsirge_<section>.css.

  Resting state is VISIBLE without JS — never write a bare
  `[data-customsirge-reveal] { opacity: 0 }`. Gate the hidden state behind the
  class this runtime sets on <html> on init (never when reduced motion is on,
  so a visitor who asked for less motion never has content hidden from them
  even for a frame):

    html.customsirge-motion [data-customsirge-reveal] {
      opacity: 0;
      transform: translateY(28px);
      transition: opacity 0.65s var(--customsirge-ease-out),
        transform 0.65s var(--customsirge-ease-out);
      transition-delay: var(--customsirge-motion-delay, 0ms);
    }
    -- HH-14 / decision 0016: side reveal is its OWN timing, not the vertical
       reveal's 28px/0.65s reused sideways — 40px travel over 0.7s. --
    html.customsirge-motion [data-customsirge-reveal="left"],
    html.customsirge-motion [data-customsirge-reveal="right"] {
      transition-duration: 0.7s;
    }
    html.customsirge-motion [data-customsirge-reveal="left"]  { transform: translateX(-40px); }
    html.customsirge-motion [data-customsirge-reveal="right"] { transform: translateX(40px); }
    -- decision 0016: a staggered grid's own children reveal over 0.55s, not
       the lone element's 0.65s — same selector specificity as the base rule
       above (0,2,1: one element + two attributes), so keep it AFTER that rule
       in your stylesheet or it will not win the tie. --
    html.customsirge-motion [data-customsirge-stagger] > [data-customsirge-reveal] {
      transition-duration: 0.55s;
    }
    html.customsirge-motion [data-customsirge-reveal][data-customsirge-motion-in] {
      opacity: 1;
      transform: none;
    }

  (The `html.customsirge-motion` prefix also gives this rule higher specificity
  than kit.css's own ungated `[data-customsirge-reveal]` rule — see the
  DUPLICATE-REGISTRATION NOTICE above — so it wins regardless of stylesheet
  order while this runtime is active.)

  Marquee — this file writes `--customsirge-motion-marquee-distance` (one
  copy's px width) and `--customsirge-motion-marquee-duration` (seconds) onto
  the track, plus `data-customsirge-motion-marquee-ready="true"` on the outer
  element once measured (or `data-customsirge-motion-static="true"` under
  reduced motion / a missing track — style that as a plain wrapped row):

    [data-customsirge-motion-marquee-ready] [data-customsirge-marquee-track] {
      animation: customsirge-motion-marquee var(--customsirge-motion-marquee-duration, 20s) linear infinite;
    }
    [data-customsirge-marquee]:hover [data-customsirge-marquee-track] {
      animation-play-state: paused;  -- the "pauses on hover" requirement — pure CSS --
    }
    @keyframes customsirge-motion-marquee {
      from { transform: translateX(0); }
      to   { transform: translateX(calc(var(--customsirge-motion-marquee-distance, 100%) * -1)); }
    }

  Float — this file writes `--customsirge-motion-float-amplitude` (px) and
  `--customsirge-motion-float-period`, plus `data-customsirge-motion-float-
  ready="true"`:

    [data-customsirge-motion-float-ready] {
      animation: customsirge-motion-float var(--customsirge-motion-float-period, 6s) ease-in-out infinite;
    }
    @keyframes customsirge-motion-float {
      0%, 100% { transform: translateY(calc(var(--customsirge-motion-float-amplitude, 8px) * -1)); }
      50%      { transform: translateY(var(--customsirge-motion-float-amplitude, 8px)); }
    }

  Countup and parallax need no section CSS — both are fully JS-driven (text
  content / inline transform).
  ─────────────────────────────────────────────────────────────────────────────
*/

(function () {
  'use strict';

  if (!window.Customsirge || typeof window.Customsirge.register !== 'function') {
    console.error('[customsirge] motion.js requires customsirge_kit.js to load first');
    return;
  }

  var Customsirge = window.Customsirge;

  function reducedMotion() {
    return Customsirge.prefersReducedMotion();
  }

  // Progressive-enhancement gate for CSS (see CSS CONTRACT above). Runs once,
  // on load, and never when the visitor asked for less motion — so a hidden
  // resting state can never be the thing a reduced-motion visitor sees.
  if (!reducedMotion()) {
    document.documentElement.classList.add('customsirge-motion');
  }

  function numAttr(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  // ---------------------------------------------------------------------------
  // Reveal (+ side reveal, + stagger's per-child delay assignment)
  // ---------------------------------------------------------------------------

  function initStagger(container) {
    var step = numAttr(container.dataset.step, 80);
    Array.prototype.forEach.call(container.children, function (child, i) {
      // Only children that already opted in with their own data-customsirge-
      // reveal get a computed delay — this attribute does not add reveal to
      // children that didn't ask for it (a stagger container may mix reveal
      // and non-reveal children, e.g. a grid with a heading that isn't
      // staggered next to cards that are).
      if (!child.hasAttribute('data-customsirge-reveal')) return;
      if (child.dataset.delay === undefined) {
        child.dataset.delay = String(i * step);
      }
    });
    // No cleanup: this only assigns attributes children read for themselves.
  }

  function initReveal(el) {
    var delayMs = numAttr(el.dataset.delay, 0);
    if (delayMs) {
      el.style.setProperty('--customsirge-motion-delay', delayMs + 'ms');
    }

    if (reducedMotion() || !('IntersectionObserver' in window)) {
      el.setAttribute('data-customsirge-motion-in', 'true');
      return undefined;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-customsirge-motion-in', 'true');
          observer.unobserve(entry.target);
        });
      },
      // threshold 0: any intersecting pixel reveals. A 10 % threshold left elements that are only
      // partly on screen by design (the 29 px card peeks in horizontal rows, pdp_related_products PRP-07)
      // at opacity 0 forever; the -10 % bottom rootMargin still keeps the reveal off the fold edge.
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );
    observer.observe(el);
    return function () {
      observer.disconnect();
    };
  }

  // ---------------------------------------------------------------------------
  // Marquee — clones the track's content and animates the track by minus one
  // copy's PITCH: the distance from an item to the same item in the first
  // clone, read from the DOM. That counts the flex gap between the last
  // original and the clone, which the old scrollWidth / 2 split in half — half
  // a gap short, a visible jump at every wrap. One clone by default, as the
  // brief asked: enough while a copy is at least as wide as the strip.
  // data-fill keeps cloning until the strip is covered at its current width,
  // so a short run of items on a wide screen never leaves the end of the strip
  // empty. Re-measured whenever the strip or its content changes size (fonts,
  // images, resize, breakpoint); a running animation keeps the distance it
  // started with, so it is restarted when the distance changes.
  // ---------------------------------------------------------------------------

  function initMarquee(el) {
    // library marquee (customsirge_marquee straight layout) uses the .customsirge-marquee__track class
    var track = el.querySelector('[data-customsirge-marquee-track]') || el.querySelector('.customsirge-marquee__track');
    if (!track || !track.children.length) return undefined;

    if (reducedMotion()) {
      el.setAttribute('data-customsirge-motion-static', 'true');
      el.setAttribute('data-customsirge-marquee-static', 'true'); // kit.css compat
      return undefined;
    }

    // One copy, taken before anything is appended. A clone is a CHILD of the track and must never be a
    // track itself: strip the track identity so section CSS keyed on [data-customsirge-marquee-track]
    // (or the motion vars) animates only the original; the clones ride along inside it
    // (home_awards_social_proof HA-11).
    var template = track.cloneNode(true);
    template.setAttribute('aria-hidden', 'true');
    template.removeAttribute('data-customsirge-marquee-track');
    template.removeAttribute('data-customsirge-marquee');
    template.setAttribute('data-customsirge-marquee-clone', 'true');
    template.style.animation = 'none';
    Array.prototype.forEach.call(template.querySelectorAll('a, button, input, [tabindex]'), function (node) {
      node.setAttribute('tabindex', '-1');
    });

    var fill = el.hasAttribute('data-fill');
    var speed = numAttr(el.dataset.speed, 60); // px/second
    var clones = [];
    var distance = 0;
    var frame = 0;

    function addClone() {
      var clone = template.cloneNode(true);
      track.appendChild(clone);
      clones.push(clone);
    }

    // First laid-out item to its twin in the first clone. Both carry the same translateX, so the
    // difference is transform-free.
    function pitch() {
      var twins = clones[0].children;
      for (var i = 0; i < twins.length; i++) {
        var item = track.children[i];
        if (item.getClientRects().length) {
          return twins[i].getBoundingClientRect().left - item.getBoundingClientRect().left;
        }
      }
      return 0;
    }

    function measure() {
      frame = 0;
      var p = Math.round(pitch() * 100) / 100;
      if (p <= 0) return; // not laid out yet (hidden); the observer calls back once it is
      if (fill) {
        // Clones spanning the strip's width, plus a spare so no sub-pixel sliver shows at the wrap.
        var need = Math.ceil(el.clientWidth / p) + 1;
        while (clones.length < need) addClone();
        while (clones.length > need) clones.pop().remove();
      }
      if (Math.abs(p - distance) < 0.5) return;
      var restart = distance > 0;
      distance = p;
      track.style.setProperty('--customsirge-motion-marquee-distance', p + 'px');
      track.style.setProperty('--customsirge-motion-marquee-duration', p / speed + 's');
      // kit.css compat (library straight marquee reads these names)
      track.style.setProperty('--customsirge-marquee-distance', p + 'px');
      track.style.setProperty('--customsirge-marquee-duration', p / speed + 's');
      el.setAttribute('data-customsirge-motion-marquee-ready', 'true');
      el.setAttribute('data-customsirge-marquee-ready', 'true');
      if (restart) {
        track.style.animationName = 'none';
        void track.offsetWidth; // flush, so dropping the override starts a fresh animation
        track.style.animationName = '';
      }
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(measure);
    }

    addClone();
    measure();

    // The strip resizing (viewport, breakpoint) or its content resizing (web fonts, images) changes the
    // pitch or the copies needed. Measured on the next frame rather than inside the observer callback,
    // because adding clones resizes the observed track.
    var observer = null;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(schedule);
      observer.observe(el);
      observer.observe(track);
    } else {
      window.addEventListener('resize', schedule);
    }
    window.addEventListener('customsirge:relayout', schedule);

    return function () {
      if (observer) observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('customsirge:relayout', schedule);
      clones.forEach(function (clone) {
        clone.remove();
      });
    };
  }

  // ---------------------------------------------------------------------------
  // Count up — locale-aware formatting via toLocaleString, so "1240" renders
  // as "1,240" (or the visitor's own grouping/decimal convention) both at rest
  // and at every animated frame, not just at the final value.
  // ---------------------------------------------------------------------------

  function initCountup(el) {
    var to = parseFloat(el.dataset.to);
    if (isNaN(to)) return undefined;

    var duration = numAttr(el.dataset.duration, 1600);
    var prefix = el.dataset.prefix || '';
    var suffix = el.dataset.suffix || '';
    var decimals = (String(el.dataset.to).split('.')[1] || '').length;
    var locale = document.documentElement.lang || undefined;

    function render(value) {
      var formatted = value.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      el.textContent = prefix + formatted + suffix;
    }

    if (reducedMotion() || !('IntersectionObserver' in window)) {
      render(to);
      return undefined;
    }

    render(0);
    var frame = null;
    var observer = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(el);
        var start = performance.now();
        (function tick(now) {
          var progress = Math.min((now - start) / duration, 1);
          var eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
          render(to * eased);
          if (progress < 1) frame = requestAnimationFrame(tick);
        })(start);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);

    return function () {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }

  // ---------------------------------------------------------------------------
  // Float — CSS-animation based: this function only ever WRITES two custom
  // properties and a ready flag, never an animation itself, so the actual
  // animating happens off the main thread in the compositor.
  // ---------------------------------------------------------------------------

  function initFloat(el) {
    if (reducedMotion()) return; // resting position — keyframe never applied

    var amplitude = numAttr(el.dataset.amplitude, 8);
    var period = el.dataset.period || '6s';
    if (/^\d+(\.\d+)?$/.test(period)) period += 's'; // bare number = seconds

    el.style.setProperty('--customsirge-motion-float-amplitude', amplitude + 'px');
    el.style.setProperty('--customsirge-motion-float-period', period);
    el.setAttribute('data-customsirge-motion-float-ready', 'true');
  }

  // ---------------------------------------------------------------------------
  // Parallax — rAF-throttled, passive scroll. Reads layout once per animation
  // frame (inside the rAF callback, batched) and writes right after — no
  // interleaved read/write thrash. Desktop-only per spec (>=990px), re-checked
  // live so a resize across the breakpoint degrades/re-engages cleanly.
  // ---------------------------------------------------------------------------

  function initParallax(el) {
    if (reducedMotion()) return undefined;

    var amount = numAttr(el.getAttribute('data-customsirge-parallax'), 40);
    var ticking = false;

    function isDesktopWide() {
      return window.matchMedia('(min-width: ' + Customsirge.breakpoint('lg') + 'px)').matches;
    }

    function update() {
      ticking = false;
      if (!isDesktopWide()) {
        el.style.transform = '';
        return;
      }
      var rect = el.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var total = vh + rect.height;
      var progress = (vh - rect.top) / total; // 0 entering the viewport, 1 leaving it
      var clamped = Math.min(1, Math.max(0, progress));
      var offset = (clamped - 0.5) * 2 * amount; // -amount .. +amount across the scroll range
      el.style.transform = 'translate3d(0,' + offset.toFixed(2) + 'px,0)';
    }

    function onTick() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    el.style.willChange = 'transform';
    update();
    window.addEventListener('scroll', onTick, { passive: true });
    window.addEventListener('resize', onTick, { passive: true });

    return function () {
      window.removeEventListener('scroll', onTick);
      window.removeEventListener('resize', onTick);
      el.style.willChange = '';
      el.style.transform = '';
    };
  }

  // ---------------------------------------------------------------------------
  // Registration — ONE component, ONE combined selector, dispatching per
  // element on whichever attribute(s) it actually carries.
  // ---------------------------------------------------------------------------

  var SELECTOR = [
    '[data-customsirge-reveal]',
    '[data-customsirge-stagger]',
    '[data-customsirge-marquee]',
    '[data-customsirge-countup]',
    '[data-customsirge-float]',
    '[data-customsirge-parallax]',
  ].join(',');

  // ONE OWNER PER ATTRIBUTE (decision 0045): kit.js ships legacy 'reveal' / 'marquee' / 'countup'
  // components bound to the same data attributes. Both binding = double clones, two count-up
  // formats racing, cloned reveal nodes stuck at opacity 0 (home_awards_social_proof loop 1).
  // This runtime supersedes them: tear them down before registering.
  ['reveal', 'marquee', 'countup'].forEach(function (legacy) {
    if (Customsirge.unregister) Customsirge.unregister(legacy);
  });

  Customsirge.register('motion', {
    selector: SELECTOR,
    init: function (el) {
      var cleanups = [];

      // Stagger runs first: it only ASSIGNS data-delay on children that
      // already carry data-customsirge-reveal, and document order guarantees
      // this container is processed before those children in the same scan.
      if (el.hasAttribute('data-customsirge-stagger')) {
        initStagger(el);
      }
      if (el.hasAttribute('data-customsirge-reveal')) {
        var revealCleanup = initReveal(el);
        if (revealCleanup) cleanups.push(revealCleanup);
      }
      if (el.hasAttribute('data-customsirge-marquee')) {
        var marqueeCleanup = initMarquee(el);
        if (marqueeCleanup) cleanups.push(marqueeCleanup);
      }
      if (el.hasAttribute('data-customsirge-countup')) {
        var countupCleanup = initCountup(el);
        if (countupCleanup) cleanups.push(countupCleanup);
      }
      if (el.hasAttribute('data-customsirge-float')) {
        initFloat(el);
      }
      if (el.hasAttribute('data-customsirge-parallax')) {
        var parallaxCleanup = initParallax(el);
        if (parallaxCleanup) cleanups.push(parallaxCleanup);
      }

      if (!cleanups.length) return undefined;
      return function () {
        cleanups.forEach(function (fn) {
          fn();
        });
      };
    },
  });
})();
